import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, NavLink, useNavigate, Navigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import {
  LayoutDashboard, Package, Vault, Star, Clock, Sparkles, Gift, Bell, ShieldCheck, Settings,
  Download, Send, LifeBuoy, LogOut, Copy, Check, Loader2, ArrowRight, Search, ExternalLink,
  Eye, EyeOff, ArrowLeftRight, ChevronRight, Mail, Trash2, MessageSquare, ShoppingBag, Wallet,
  TrendingUp, Award, CheckCircle2, Info,
} from 'lucide-react';
import { BRANDS as brandsCatalog } from '../mock';
import ReferralsTab from '../components/ReferralsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------------- Nav config --------------
const SECTIONS = [
  { key: 'overview',      label: 'Overview',       Icon: LayoutDashboard, path: '',            group: 'primary' },
  { key: 'orders',        label: 'Orders',         Icon: Package,          path: 'orders',      group: 'primary' },
  { key: 'vault',         label: 'Code Vault',     Icon: Vault,            path: 'vault',       group: 'primary' },
  { key: 'favorites',     label: 'Favorites',      Icon: Star,             path: 'favorites',   group: 'primary' },
  { key: 'recent',        label: 'Recently viewed', Icon: Clock,           path: 'recent',      group: 'primary' },
  { key: 'referrals',     label: 'Referrals',      Icon: Sparkles,         path: 'referrals',   group: 'primary' },
  { key: 'gift',          label: 'Gift a code',    Icon: Gift,             path: 'gift',        group: 'secondary' },
  { key: 'export',        label: 'Export codes',   Icon: Download,         path: 'export',      group: 'secondary' },
  { key: 'notifications', label: 'Notifications',  Icon: Bell,             path: 'notifications', group: 'settings' },
  { key: 'preferences',   label: 'Preferences',    Icon: Settings,         path: 'preferences', group: 'settings' },
  { key: 'security',      label: 'Security',       Icon: ShieldCheck,      path: 'security',    group: 'settings' },
  { key: 'support',       label: 'Support',        Icon: LifeBuoy,         path: 'support',     group: 'settings' },
];
const MOBILE_TABS = ['overview', 'orders', 'vault', 'referrals', 'support'];

// ============================================================================
// LAYOUT — desktop sidebar + mobile bottom-tab bar
// ============================================================================
const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRoot = location.pathname === '/dashboard' || location.pathname === '/dashboard/';

  const initials = (user?.name || 'U').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  const NavItem = ({ item }) => {
    const to = item.path ? `/dashboard/${item.path}` : '/dashboard';
    const active = item.path ? location.pathname.startsWith(to) : isRoot;
    return (
      <NavLink
        to={to}
        end={!item.path}
        data-testid={`nav-${item.key}`}
        className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all
          ${active ? 'bg-[#8247E5]/10 text-[#8247E5]' : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'}`}
      >
        <item.Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#8247E5]' : ''}`} />
        <span className="truncate">{item.label}</span>
        {active && <ChevronRight className="w-3.5 h-3.5 ml-auto text-[#8247E5]" />}
      </NavLink>
    );
  };

  const groups = ['primary', 'secondary', 'settings'];
  const groupLabels = { primary: 'Overview', secondary: 'Actions', settings: 'Settings' };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="dashboard-root">
      <div className="max-w-[1400px] mx-auto flex">
        {/* Sidebar — desktop only */}
        <aside className="hidden lg:flex w-[260px] shrink-0 flex-col border-r border-neutral-200 bg-white sticky top-0 h-screen">
          <div className="p-5 border-b border-neutral-100">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} className="w-10 h-10 rounded-full object-cover shrink-0" alt="" />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: 'linear-gradient(135deg, #8247E5, #A855F7)' }}>
                  {initials}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{user?.name || 'Account'}</div>
                <div className="text-[11px] text-neutral-500 truncate">{user?.email}</div>
              </div>
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
            {groups.map(g => (
              <div key={g}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 px-3 mb-1.5">{groupLabels[g]}</div>
                <div className="space-y-0.5">
                  {SECTIONS.filter(s => s.group === g).map(s => <NavItem key={s.key} item={s} />)}
                </div>
              </div>
            ))}
          </nav>
          <div className="p-3 border-t border-neutral-100">
            <button
              onClick={() => { logout(); navigate('/'); }}
              data-testid="dashboard-logout-btn"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 lg:py-10 pb-24 lg:pb-10">
          {children}
        </main>
      </div>

      {/* Mobile bottom-tab bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40" data-testid="mobile-bottom-tabs">
        <div className="grid grid-cols-5">
          {MOBILE_TABS.map(k => {
            const s = SECTIONS.find(x => x.key === k);
            const to = s.path ? `/dashboard/${s.path}` : '/dashboard';
            const active = s.path ? location.pathname.startsWith(to) : isRoot;
            return (
              <NavLink
                key={k}
                to={to}
                end={!s.path}
                data-testid={`mtab-${s.key}`}
                className={`flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold ${active ? 'text-[#8247E5]' : 'text-neutral-500'}`}>
                <s.Icon className="w-5 h-5" />
                {s.label.split(' ')[0]}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

// ============================================================================
// Shared bits
// ============================================================================
const SectionHeader = ({ title, subtitle, action }) => (
  <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="section-title">{title}</h1>
      {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const Card = ({ children, className = '', ...rest }) => (
  <div className={`bg-white rounded-2xl border border-neutral-200 p-5 md:p-6 ${className}`} {...rest}>{children}</div>
);

const EmptyState = ({ Icon, title, description, cta }) => (
  <div className="text-center py-16 px-6" data-testid="empty-state">
    <div className="w-14 h-14 mx-auto rounded-full bg-neutral-100 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-neutral-400" />
    </div>
    <div className="font-semibold text-neutral-800">{title}</div>
    <div className="text-sm text-neutral-500 mt-1 max-w-sm mx-auto">{description}</div>
    {cta && <div className="mt-5">{cta}</div>}
  </div>
);

// ============================================================================
// OVERVIEW
// ============================================================================
const OverviewSection = () => {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/user/stats`).then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, []);

  const statBlocks = useMemo(() => stats ? [
    { label: 'Lifetime spend',   value: formatPrice(stats.lifetime_spend_usd), Icon: TrendingUp, tone: 'bg-emerald-50 text-emerald-700' },
    { label: 'Total orders',     value: stats.total_orders,                    Icon: Package,    tone: 'bg-sky-50 text-sky-700' },
    { label: 'Codes in vault',   value: stats.saved_codes_count,               Icon: Vault,      tone: 'bg-violet-50 text-violet-700' },
    { label: 'Active promos',    value: stats.active_promos,                   Icon: Award,      tone: 'bg-amber-50 text-amber-700' },
  ] : [], [stats, formatPrice]);

  return (
    <>
      <SectionHeader
        title={`Welcome back, ${(user?.name || 'friend').split(' ')[0]}`}
        subtitle="Everything about your TokenGate account in one place."
      />

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-neutral-200 p-5 h-[110px] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {statBlocks.map(s => (
            <div key={s.label} className="bg-white rounded-2xl border border-neutral-200 p-4 md:p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${s.tone}`}>
                <s.Icon className="w-4 h-4" />
              </div>
              <div className="text-xs text-neutral-500 font-medium">{s.label}</div>
              <div className="text-xl md:text-2xl font-bold text-neutral-900 mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <Link to="/browse" className="group bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-2xl" />
          <ShoppingBag className="w-6 h-6 mb-3" />
          <div className="font-semibold text-lg">Explore gift cards</div>
          <div className="text-sm text-white/80 mt-1">40+ brands, delivered by email in minutes.</div>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium">Browse <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" /></div>
        </Link>
        <Link to="/swap" className="group bg-white rounded-2xl border border-neutral-200 p-6 hover:border-neutral-300 transition-colors">
          <ArrowLeftRight className="w-6 h-6 mb-3 text-neutral-900" />
          <div className="font-semibold text-lg">Swap tokens</div>
          <div className="text-sm text-neutral-500 mt-1">Wallet-to-wallet at real market rates. Zero KYC.</div>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-neutral-900">Open swap <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" /></div>
        </Link>
        <NavLink to="/dashboard/referrals" className="group bg-white rounded-2xl border border-neutral-200 p-6 hover:border-neutral-300 transition-colors">
          <Sparkles className="w-6 h-6 mb-3 text-[#8247E5]" />
          <div className="font-semibold text-lg">Refer & Earn</div>
          <div className="text-sm text-neutral-500 mt-1">Get 10% off for every friend that signs up and buys.</div>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#8247E5]">Get your link <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" /></div>
        </NavLink>
      </div>
    </>
  );
};

// ============================================================================
// ORDERS (reuse existing logic)
// ============================================================================
const OrdersSection = () => {
  const { formatPrice } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/orders/mine`).then(({ data }) => setOrders(data)).finally(() => setLoading(false));
  }, []);

  const filtered = orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (q && !JSON.stringify(o).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <SectionHeader title="Orders" subtitle={`${orders.length} order${orders.length === 1 ? '' : 's'} total`} />
      <div className="mb-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search orders, brands, order IDs…" className="pl-9" data-testid="orders-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-10 px-3 rounded-md border border-neutral-200 bg-white text-sm" data-testid="orders-status-filter">
          <option value="all">All statuses</option>
          <option value="pending_payment">Pending payment</option>
          <option value="paid">Paid</option>
          <option value="delivered">Delivered</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
       : filtered.length === 0 ? <EmptyState Icon={Package} title="No orders yet" description="Your gift card purchases will show up here." cta={<Link to="/browse"><Button className="bg-[#8247E5] hover:bg-[#6d33cc]">Browse gift cards</Button></Link>} />
       : (
        <div className="space-y-3" data-testid="orders-list">
          {filtered.map(o => (
            <Link to={`/order/${o.id}`} key={o.id} className="block group">
              <Card className="hover:border-[#8247E5]/40 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${statusStyle(o.status)}`}>{o.status.replace('_', ' ')}</span>
                      <span className="text-[11px] text-neutral-500 font-mono">{o.id.slice(-8)}</span>
                    </div>
                    <div className="text-sm font-medium text-neutral-800 truncate">
                      {(o.items || []).map(i => `${i.brand_name} $${i.denomination}×${i.quantity}`).join(' · ')}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-neutral-900">{formatPrice(o.subtotal_usd)}</div>
                    <ChevronRight className="w-4 h-4 text-neutral-300 mt-1 ml-auto group-hover:text-[#8247E5]" />
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
       )}
    </>
  );
};

const statusStyle = s => ({
  pending_payment: 'bg-amber-50 text-amber-700',
  confirming: 'bg-sky-50 text-sky-700',
  paid: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-neutral-100 text-neutral-600',
  partially_paid: 'bg-amber-50 text-amber-700',
  expired: 'bg-rose-50 text-rose-700',
}[s] || 'bg-neutral-100 text-neutral-600');

// ============================================================================
// VAULT (delivered gift codes)
// ============================================================================
const VaultSection = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState({});
  const [copied, setCopied] = useState(null);
  const [showRedeemed, setShowRedeemed] = useState(false);
  const { toast } = useToast();

  const load = () => { axios.get(`${API}/user/vault`).then(({ data }) => setCodes(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const toggleRedeemed = async (c) => {
    try {
      await axios.post(`${API}/user/vault/${c.order_id}/${c.idx}/redeem`, { redeemed: !c.redeemed });
      setCodes(prev => prev.map(x => (x.order_id === c.order_id && x.idx === c.idx) ? { ...x, redeemed: !c.redeemed } : x));
    } catch { toast({ title: 'Could not update', variant: 'destructive' }); }
  };

  const copy = async (val, key) => { try { await navigator.clipboard.writeText(val); setCopied(key); setTimeout(() => setCopied(null), 1200); } catch {} };

  const visible = codes.filter(c => showRedeemed || !c.redeemed);

  return (
    <>
      <SectionHeader
        title="Code Vault"
        subtitle="All your delivered gift codes in one place. Mark them as redeemed to keep things tidy."
        action={
          <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
            <Switch checked={showRedeemed} onCheckedChange={setShowRedeemed} data-testid="show-redeemed-toggle" />
            <span>Show redeemed</span>
          </label>
        }
      />
      {loading ? <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
       : visible.length === 0 ? <EmptyState Icon={Vault} title={codes.length === 0 ? 'Your vault is empty' : 'All codes redeemed 🎉'} description={codes.length === 0 ? 'Delivered gift codes will show up here for easy access.' : 'Flip the toggle to see redeemed ones.'} cta={codes.length === 0 && <Link to="/browse"><Button className="bg-[#8247E5] hover:bg-[#6d33cc]">Buy your first card</Button></Link>} />
       : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="vault-list">
          {visible.map(c => {
            const revealKey = `${c.order_id}:${c.idx}`;
            const isReveal = !!reveal[revealKey];
            return (
              <Card key={revealKey} className={c.redeemed ? 'opacity-60' : ''}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">{new Date(c.order_date).toLocaleDateString()}</div>
                    <div className="font-semibold">{c.brand_name} · ${c.denomination}</div>
                  </div>
                  {c.redeemed && <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Redeemed</span>}
                </div>
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Code</div>
                  <div className="font-mono text-sm text-neutral-900 break-all">{isReveal ? c.code : '•••• •••• •••• ' + (c.code.slice(-4) || '')}</div>
                  {c.pin && isReveal && <div className="mt-2 text-xs"><span className="text-neutral-500">PIN: </span><span className="font-mono">{c.pin}</span></div>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setReveal(p => ({ ...p, [revealKey]: !isReveal }))} className="flex-1 gap-1.5" data-testid="vault-reveal-btn">
                    {isReveal ? <><EyeOff className="w-3.5 h-3.5" /> Hide</> : <><Eye className="w-3.5 h-3.5" /> Reveal</>}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copy(c.code, revealKey)} className="flex-1 gap-1.5" data-testid="vault-copy-btn">
                    {copied === revealKey ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleRedeemed(c)} data-testid="vault-toggle-redeemed">
                    {c.redeemed ? 'Unmark' : 'Mark redeemed'}
                  </Button>
                </div>
                {c.instructions && <div className="mt-3 text-[11px] text-neutral-500 line-clamp-2">{c.instructions}</div>}
              </Card>
            );
          })}
        </div>
       )}
    </>
  );
};

// ============================================================================
// FAVORITES
// ============================================================================
const FavoritesSection = () => {
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { axios.get(`${API}/user/favorites`).then(({ data }) => setFavs(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const remove = async (slug) => {
    await axios.delete(`${API}/user/favorites/${slug}`);
    setFavs(prev => prev.filter(f => f.slug !== slug));
  };
  return (
    <>
      <SectionHeader title="Favorites" subtitle="Your starred brands for one-tap re-buying." />
      {loading ? <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
       : favs.length === 0 ? <EmptyState Icon={Star} title="No favorites yet" description="Star a brand from the browse page to keep it here for quick access." cta={<Link to="/browse"><Button className="bg-[#8247E5] hover:bg-[#6d33cc]">Browse brands</Button></Link>} />
       : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="favorites-grid">
          {favs.map(f => {
            const brand = brandsCatalog.find(b => b.slug === f.slug);
            const productHref = `/product/${brand?.id || f.slug}`;
            return (
              <Card key={f.slug} className="p-4">
                <Link to={productHref} className="block" data-testid={`favorite-tile-${f.slug}`}>
                  <div className="aspect-square rounded-lg bg-white border border-neutral-200 mb-3 flex items-center justify-center overflow-hidden p-4">
                    {f.logo_url || brand?.logoUrl ? <img src={f.logo_url || brand.logoUrl} alt={f.name} className="max-w-full max-h-full object-contain" /> : <ShoppingBag className="w-8 h-8 text-neutral-300" />}
                  </div>
                  <div className="font-medium text-sm truncate mb-2">{f.name || brand?.name || f.slug}</div>
                </Link>
                <div className="flex gap-2">
                  <Link to={productHref} className="flex-1"><Button variant="outline" size="sm" className="w-full">Buy</Button></Link>
                  <Button variant="ghost" size="sm" onClick={() => remove(f.slug)} data-testid="fav-remove-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
       )}
    </>
  );
};

// ============================================================================
// RECENTLY VIEWED
// ============================================================================
const RecentSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API}/user/recent`).then(({ data }) => setItems(data)).finally(() => setLoading(false)); }, []);
  return (
    <>
      <SectionHeader title="Recently viewed" subtitle="Brands you've browsed in the last few days." />
      {loading ? <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
       : items.length === 0 ? <EmptyState Icon={Clock} title="Nothing here yet" description="Brands you view on the browse page will show up here." />
       : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="recent-grid">
          {items.map(i => {
            const brand = brandsCatalog.find(b => b.slug === i.slug);
            const productHref = `/product/${brand?.id || i.slug}`;
            return (
              <Link key={i.slug} to={productHref} data-testid={`recent-tile-${i.slug}`}>
                <Card className="p-4 hover:border-[#8247E5]/40">
                  <div className="aspect-square rounded-lg bg-white border border-neutral-200 mb-3 flex items-center justify-center overflow-hidden p-4">
                    {i.logo_url || brand?.logoUrl ? <img src={i.logo_url || brand.logoUrl} alt={i.name} className="max-w-full max-h-full object-contain" /> : <ShoppingBag className="w-8 h-8 text-neutral-300" />}
                  </div>
                  <div className="font-medium text-sm truncate">{i.name || brand?.name || i.slug}</div>
                </Card>
              </Link>
            );
          })}
        </div>
       )}
    </>
  );
};

// ============================================================================
// REFERRALS (delegated to legacy component)
// ============================================================================
const ReferralsSection = () => (
  <>
    <SectionHeader title="Refer & Earn" subtitle="Get 10% off for every friend who signs up and buys." />
    <ReferralsTab />
  </>
);

// ============================================================================
// GIFT A CODE
// ============================================================================
const GiftSection = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  useEffect(() => {
    axios.get(`${API}/user/vault`).then(({ data }) => setCodes(data.filter(c => !c.redeemed))).finally(() => setLoading(false));
  }, []);
  const submit = async () => {
    if (!selected || !email) return;
    try {
      setSubmitting(true);
      await axios.post(`${API}/user/gift-code`, { order_id: selected.order_id, idx: selected.idx, recipient_email: email, recipient_name: name || null, message: message || null });
      toast({ title: 'Gift sent!', description: `We emailed ${email} the gift card.` });
      setSelected(null); setEmail(''); setName(''); setMessage('');
    } catch (e) { toast({ title: 'Could not send', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };
  return (
    <>
      <SectionHeader title="Gift a code" subtitle="Send a purchased gift card straight to someone's inbox." />
      {loading ? <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
       : codes.length === 0 ? <EmptyState Icon={Gift} title="No codes to gift" description="Only purchased-and-not-yet-redeemed codes can be gifted. Buy a card first." cta={<Link to="/browse"><Button className="bg-[#8247E5] hover:bg-[#6d33cc]">Browse gift cards</Button></Link>} />
       : (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <div className="text-sm font-semibold mb-3">1. Pick a code to gift</div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="gift-codes-list">
              {codes.map(c => {
                const k = `${c.order_id}:${c.idx}`;
                const selKey = selected && `${selected.order_id}:${selected.idx}`;
                return (
                  <button key={k} onClick={() => setSelected(c)} className={`w-full text-left p-3 rounded-lg border transition-all ${selKey === k ? 'border-[#8247E5] bg-[#8247E5]/5' : 'border-neutral-200 hover:border-neutral-300'}`} data-testid="gift-code-option">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-sm">{c.brand_name}</div>
                        <div className="text-xs text-neutral-500">${c.denomination}</div>
                      </div>
                      {selKey === k && <Check className="w-4 h-4 text-[#8247E5]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
          <Card>
            <div className="text-sm font-semibold mb-3">2. Send it</div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Recipient email</Label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="friend@example.com" data-testid="gift-email-input" />
              </div>
              <div>
                <Label className="text-xs">Their name (optional)</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Alex" data-testid="gift-name-input" />
              </div>
              <div>
                <Label className="text-xs">Message (optional)</Label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Happy birthday!" className="w-full h-24 border border-neutral-200 rounded-md px-3 py-2 text-sm resize-none" data-testid="gift-message-input" />
              </div>
              <Button disabled={!selected || !email || submitting} onClick={submit} className="w-full bg-[#8247E5] hover:bg-[#6d33cc] gap-2" data-testid="gift-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send the gift</>}
              </Button>
              <p className="text-[11px] text-neutral-400 leading-relaxed">Once sent, the recipient can redeem the code as normal. Only send codes you're OK giving up. This doesn't hide your copy from your vault, but the recipient can use it first.</p>
            </div>
          </Card>
        </div>
       )}
    </>
  );
};

// ============================================================================
// EXPORT CODES CSV
// ============================================================================
const ExportSection = () => {
  const download = () => {
    const token = JSON.parse(localStorage.getItem('tokengate_auth') || '{}').token;
    const url = `${API}/user/export/codes.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `tokengate-codes-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
      });
  };
  return (
    <>
      <SectionHeader title="Export codes" subtitle="Download all your delivered gift codes as a CSV. Useful for bulk gifting or record-keeping." />
      <Card className="text-center py-12">
        <Download className="w-8 h-8 mx-auto text-neutral-400 mb-3" />
        <div className="font-semibold">Download codes.csv</div>
        <p className="text-sm text-neutral-500 mt-1 mb-5 max-w-md mx-auto">Includes: order date, brand, denomination, code, PIN, and redemption instructions for every delivered order.</p>
        <Button onClick={download} className="bg-[#8247E5] hover:bg-[#6d33cc] gap-2" data-testid="export-csv-btn"><Download className="w-4 h-4" /> Download CSV</Button>
      </Card>
    </>
  );
};

// ============================================================================
// NOTIFICATIONS
// ============================================================================
const NotificationsSection = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  useEffect(() => { axios.get(`${API}/user/notifications`).then(({ data }) => setPrefs(data)).finally(() => setLoading(false)); }, []);
  const save = async (next) => {
    setSaving(true);
    try { await axios.put(`${API}/user/notifications`, next); setPrefs(next); toast({ title: 'Preferences saved' }); }
    finally { setSaving(false); }
  };
  const rows = [
    { k: 'order_updates', label: 'Order updates', desc: 'Payment received, partial payment, gift codes delivered.' },
    { k: 'referral_rewards', label: 'Referral rewards', desc: 'When you earn a reward from a successful referral.' },
    { k: 'marketing', label: 'Special offers', desc: 'Occasional welcome offers and holiday promotions.' },
    { k: 'product_news', label: 'Product news', desc: 'New brands, new supported coins, feature launches.' },
  ];
  return (
    <>
      <SectionHeader title="Notifications" subtitle="Choose which emails you want to receive from TokenGate." />
      {loading || !prefs ? <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div> : (
        <Card>
          <div className="divide-y divide-neutral-100">
            {rows.map(r => (
              <div key={r.k} className="flex items-center justify-between py-4 first:pt-0 last:pb-0" data-testid={`notif-${r.k}`}>
                <div className="pr-4">
                  <div className="font-medium text-sm">{r.label}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{r.desc}</div>
                </div>
                <Switch checked={prefs[r.k]} disabled={saving || r.k === 'order_updates'} onCheckedChange={v => save({ ...prefs, [r.k]: v })} />
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-100 text-[11px] text-neutral-400 leading-relaxed">Order updates cannot be disabled. They contain your gift codes.</div>
        </Card>
      )}
    </>
  );
};

// ============================================================================
// PREFERENCES
// ============================================================================
const PreferencesSection = () => {
  const [prefs, setPrefs] = useState({ default_pay_currency: '', default_display_currency: '' });
  const [saving, setSaving] = useState(false);
  const { setCountry: setCurrencyCountry } = useCurrency();
  const { toast } = useToast();
  useEffect(() => { axios.get(`${API}/user/preferences`).then(({ data }) => setPrefs({ default_pay_currency: data.default_pay_currency || '', default_display_currency: data.default_display_currency || '' })); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/preferences`, prefs);
      // Apply display currency to the site immediately.
      const map = { USD: 'US', GBP: 'GB', EUR: 'DE', INR: 'IN', BRL: 'BR', JPY: 'JP', CAD: 'CA', AUD: 'AU' };
      if (prefs.default_display_currency && map[prefs.default_display_currency]) {
        setCurrencyCountry(map[prefs.default_display_currency]);
      }
      toast({ title: 'Preferences saved' });
    } finally { setSaving(false); }
  };
  return (
    <>
      <SectionHeader title="Preferences" subtitle="Personalize your checkout experience." />
      <Card>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label>Default payment coin</Label>
            <select value={prefs.default_pay_currency} onChange={e => setPrefs({ ...prefs, default_pay_currency: e.target.value })} className="w-full h-10 px-3 rounded-md border border-neutral-200 bg-white text-sm mt-1" data-testid="pref-coin">
              <option value="">Ask each time</option>
              <option value="btc">Bitcoin (BTC)</option>
              <option value="eth">Ethereum (ETH)</option>
              <option value="usdt">Tether (USDT)</option>
              <option value="usdc">USD Coin (USDC)</option>
              <option value="sol">Solana (SOL)</option>
              <option value="bnb">BNB</option>
              <option value="ltc">Litecoin (LTC)</option>
              <option value="doge">Dogecoin (DOGE)</option>
            </select>
            <p className="text-xs text-neutral-500 mt-1">We'll preselect this coin at checkout.</p>
          </div>
          <div>
            <Label>Display currency</Label>
            <select value={prefs.default_display_currency} onChange={e => setPrefs({ ...prefs, default_display_currency: e.target.value })} className="w-full h-10 px-3 rounded-md border border-neutral-200 bg-white text-sm mt-1" data-testid="pref-currency">
              <option value="">USD (default)</option>
              <option value="INR">INR (₹)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="AUD">AUD</option>
              <option value="CAD">CAD</option>
            </select>
            <p className="text-xs text-neutral-500 mt-1">All prices will be shown in this currency.</p>
          </div>
        </div>
        <div className="mt-6">
          <Button onClick={save} disabled={saving} className="bg-[#8247E5] hover:bg-[#6d33cc]" data-testid="pref-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save preferences'}</Button>
        </div>
      </Card>
    </>
  );
};

// ============================================================================
// SECURITY
// ============================================================================
const SecuritySection = () => {
  const { user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmNext, setConfirmNext] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();
  const isGoogleUser = user?.provider === 'google';
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (next !== confirmNext) { setError('Passwords do not match'); return; }
    try {
      setSaving(true);
      await axios.post(`${API}/user/change-password`, { current_password: current || null, new_password: next });
      toast({ title: isGoogleUser ? 'Password set!' : 'Password changed' });
      setCurrent(''); setNext(''); setConfirmNext('');
    } catch (e) { setError(e?.response?.data?.detail || 'Could not update password'); }
    finally { setSaving(false); }
  };
  return (
    <>
      <SectionHeader title="Security" subtitle="Manage your password and sign-in methods." />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <div className="font-semibold mb-1">{isGoogleUser ? 'Add a password' : 'Change password'}</div>
          <p className="text-xs text-neutral-500 mb-4">{isGoogleUser ? 'You currently sign in with Google. Add a password to also sign in with email.' : 'Use at least 6 characters. A mix of letters, numbers, and symbols is best.'}</p>
          <form onSubmit={submit} className="space-y-3">
            {!isGoogleUser && (
              <div>
                <Label className="text-xs">Current password</Label>
                <Input type="password" value={current} onChange={e => setCurrent(e.target.value)} data-testid="sec-current-pw" />
              </div>
            )}
            <div>
              <Label className="text-xs">New password</Label>
              <Input type="password" value={next} onChange={e => setNext(e.target.value)} data-testid="sec-new-pw" />
            </div>
            <div>
              <Label className="text-xs">Confirm new password</Label>
              <Input type="password" value={confirmNext} onChange={e => setConfirmNext(e.target.value)} data-testid="sec-confirm-pw" />
            </div>
            {error && <div className="text-xs text-rose-600 flex items-start gap-1.5 bg-rose-50 p-2 rounded"><Info className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span></div>}
            <Button type="submit" disabled={saving} className="w-full bg-[#8247E5] hover:bg-[#6d33cc]" data-testid="sec-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isGoogleUser ? 'Set password' : 'Update password')}</Button>
          </form>
        </Card>
        <Card>
          <div className="font-semibold mb-1">Sign-in methods</div>
          <p className="text-xs text-neutral-500 mb-4">How you can access this account.</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 border border-neutral-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-neutral-400" />
                <div>
                  <div className="text-sm font-medium">Email + password</div>
                  <div className="text-xs text-neutral-500">{user?.email}</div>
                </div>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">Active</span>
            </div>
            {isGoogleUser && (
              <div className="flex items-center justify-between p-3 border border-neutral-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-red-500" />
                  <div>
                    <div className="text-sm font-medium">Google</div>
                    <div className="text-xs text-neutral-500">{user.email}</div>
                  </div>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">Linked</span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

// ============================================================================
// SUPPORT
// ============================================================================
const SupportSection = () => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [orderId, setOrderId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const { toast } = useToast();
  useEffect(() => { axios.get(`${API}/user/support/tickets`).then(({ data }) => setTickets(data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!subject || !message) return;
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/user/support/tickets`, { subject, message, category, order_id: orderId || null });
      toast({ title: 'Ticket submitted', description: `We'll reply to your email within 24 hours.` });
      setSubject(''); setMessage(''); setOrderId('');
      setTickets(prev => [{ id: data.ticket_id, subject, category, status: 'open', created_at: new Date().toISOString(), message }, ...prev]);
    } finally { setSubmitting(false); }
  };
  return (
    <>
      <SectionHeader title="Support" subtitle="Need help? Send us a message and we'll get back within 24 hours." />
      <div className="grid lg:grid-cols-[1fr,320px] gap-4">
        <Card>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. My gift code isn't working" data-testid="support-subject-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <select value={category} onChange={e => setCategory(e.target.value)} className="w-full h-10 px-3 rounded-md border border-neutral-200 bg-white text-sm" data-testid="support-category-input">
                  <option value="general">General question</option>
                  <option value="order">Order issue</option>
                  <option value="payment">Payment issue</option>
                  <option value="account">Account / login</option>
                  <option value="feedback">Feedback / feature request</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Order ID (optional)</Label>
                <Input value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="TG-xxxx" data-testid="support-order-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full h-32 border border-neutral-200 rounded-md px-3 py-2 text-sm resize-none" placeholder="Tell us what's going on…" data-testid="support-message-input" />
            </div>
            <Button type="submit" disabled={submitting || !subject || !message} className="bg-[#8247E5] hover:bg-[#6d33cc] gap-2" data-testid="support-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageSquare className="w-4 h-4" /> Submit ticket</>}
            </Button>
          </form>
        </Card>
        <div className="space-y-3">
          <Card>
            <div className="font-semibold text-sm mb-2">Common issues</div>
            <div className="space-y-2 text-xs">
              <a href="mailto:support@tokengate.in" className="flex items-center justify-between p-2 rounded hover:bg-neutral-50">Email support directly <ExternalLink className="w-3 h-3 text-neutral-400" /></a>
              <Link to="/#faq" className="flex items-center justify-between p-2 rounded hover:bg-neutral-50">FAQ / Help center <ExternalLink className="w-3 h-3 text-neutral-400" /></Link>
            </div>
          </Card>
          {tickets.length > 0 && (
            <Card>
              <div className="font-semibold text-sm mb-2">Your tickets</div>
              <div className="space-y-2" data-testid="support-tickets-list">
                {tickets.slice(0, 5).map(t => (
                  <div key={t.id} className="text-xs p-2 rounded bg-neutral-50">
                    <div className="font-medium truncate">{t.subject}</div>
                    <div className="text-neutral-500 mt-0.5 flex justify-between">
                      <span>{new Date(t.created_at).toLocaleDateString()}</span>
                      <span className="uppercase font-bold tracking-widest text-[9px]">{t.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// ROOT
// ============================================================================
const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && !user) navigate('/'); }, [user, loading, navigate]);
  if (loading || !user) return <div className="max-w-4xl mx-auto p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>;
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<OverviewSection />} />
        <Route path="/orders" element={<OrdersSection />} />
        <Route path="/vault" element={<VaultSection />} />
        <Route path="/favorites" element={<FavoritesSection />} />
        <Route path="/recent" element={<RecentSection />} />
        <Route path="/referrals" element={<ReferralsSection />} />
        <Route path="/gift" element={<GiftSection />} />
        <Route path="/export" element={<ExportSection />} />
        <Route path="/notifications" element={<NotificationsSection />} />
        <Route path="/preferences" element={<PreferencesSection />} />
        <Route path="/security" element={<SecuritySection />} />
        <Route path="/support" element={<SupportSection />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </DashboardLayout>
  );
};

export default Dashboard;
